var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { lighten, darken, styled, } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { Input } from '../input';
import { DEFAULT_CHIP_PROPS, MultiSelectSelectedTags, } from './multi-select-selected-tags';
import styles from './multi-select.module.css';
import clsx from 'clsx';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
export const MultiSelect = (_a) => {
    var { renderInput, inputProps, defaultValue, value: valueProp, options, onChange: onChangeProp, multiple, allowSelectAll, useCheckboxOption, shouldGroup = false, error, classes } = _a, props = __rest(_a, ["renderInput", "inputProps", "defaultValue", "value", "options", "onChange", "multiple", "allowSelectAll", "useCheckboxOption", "shouldGroup", "error", "classes"]);
    const isControlled = typeof valueProp !== 'undefined';
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const value = isControlled ? valueProp : internalValue;
    const { t } = useTranslation();
    const selectAllLabel = t('select.select-all', 'Select All...');
    const icon = _jsx(CheckBoxOutlineBlankIcon, { fontSize: "small" });
    const checkedIcon = _jsx(CheckBoxIcon, { fontSize: "small" });
    const renderInputCb = useMemo(() => {
        if (!renderInput) {
            // eslint-disable-next-line react/display-name
            return (params) => {
                return (_jsx(Input, Object.assign({}, params, { error: error }, inputProps)));
            };
        }
        return renderInput;
    }, [inputProps, renderInput, error]);
    const convertMultipleValueToOptions = (value) => {
        if (!(value === null || value === void 0 ? void 0 : value.length) || !(options === null || options === void 0 ? void 0 : options.length)) {
            return [];
        }
        return value
            .map((itemValue) => options.find((option) => { var _a; return ((_a = option.value) === null || _a === void 0 ? void 0 : _a.toString()) === (itemValue === null || itemValue === void 0 ? void 0 : itemValue.toString()); }))
            .filter(Boolean);
    };
    const convertSingleValueToOption = (value) => {
        if (typeof value === 'undefined') {
            return undefined;
        }
        if (value === null) {
            return null;
        }
        return options === null || options === void 0 ? void 0 : options.find((option) => { var _a; return ((_a = option.value) === null || _a === void 0 ? void 0 : _a.toString()) === (value === null || value === void 0 ? void 0 : value.toString()); });
    };
    const defaultValueHandled = useMemo(() => {
        if (multiple && Array.isArray(defaultValue)) {
            return convertMultipleValueToOptions(defaultValue);
        }
        if (!multiple && !Array.isArray(defaultValue)) {
            return convertSingleValueToOption(defaultValue);
        }
        return undefined;
    }, [multiple, defaultValue, options]);
    const valueHandled = useMemo(() => {
        // make select controlled
        if (multiple && (Array.isArray(value) || allowSelectAll)) {
            return convertMultipleValueToOptions(value);
        }
        if (!multiple && !Array.isArray(value)) {
            return convertSingleValueToOption(value);
        }
        return undefined;
    }, [multiple, value, options]);
    const onChange = (event, newValue, reason) => {
        const convertedValue = !Array.isArray(newValue)
            ? typeof (newValue === null || newValue === void 0 ? void 0 : newValue.value) === 'boolean'
                ? newValue === null || newValue === void 0 ? void 0 : newValue.value
                : (newValue === null || newValue === void 0 ? void 0 : newValue.value) || null
            : newValue.map((item) => item.value);
        let handledValue = convertedValue;
        // "select all" selected case
        if (allowSelectAll && newValue.find((option) => option.all)) {
            // should select all or deselect all
            const isToSelectAll = (value === null || value === void 0 ? void 0 : value.length) !== options.length;
            handledValue = isToSelectAll ? options.map((option) => option.value) : [];
        }
        const extendedEvent = Object.assign(Object.assign({}, event), { target: Object.assign(Object.assign({}, event.target), { value: handledValue, name: inputProps === null || inputProps === void 0 ? void 0 : inputProps.name }) });
        if (!isControlled) {
            setInternalValue(handledValue);
        }
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(extendedEvent, newValue, reason);
    };
    const filteredOptions = (options, state) => {
        const filter = createFilterOptions();
        const filtered = filter(options, state);
        if (!allowSelectAll) {
            return filtered;
        }
        return [{ name: selectAllLabel, all: true }, ...filtered];
    };
    // TODO: Refactor styling to use base components + css
    const GroupHeader = styled('div')(({ theme }) => ({
        position: 'sticky',
        top: '-8px',
        padding: '4px 10px',
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.mode === 'light'
            ? lighten(theme.palette.primary.light, 0.85)
            : darken(theme.palette.primary.main, 0.8),
    }));
    // TODO: Refactor styling to use base components + css
    const GroupItems = styled('ul')({
        padding: 0,
    });
    return (_jsx(Autocomplete, Object.assign({ multiple: multiple, getOptionLabel: (option) => option.name, getOptionKey: (option) => option.value, filterSelectedOptions: false, renderInput: renderInputCb, options: options, defaultValue: defaultValueHandled, value: valueHandled, onChange: onChange, ChipProps: DEFAULT_CHIP_PROPS, filterOptions: filteredOptions, classes: Object.assign(Object.assign({ listbox: clsx({ [styles.listboxWithSelectAll]: allowSelectAll }) }, classes), { root: 'test' }), renderOption: useCheckboxOption
            ? (props, option, { selected }) => (_jsxs("li", Object.assign({}, props, { children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: { marginRight: 8 }, checked: selected ||
                            (option.all &&
                                (valueHandled === null || valueHandled === void 0 ? void 0 : valueHandled.length) === options.length) }), option.name] })))
            : props.renderOption, renderTags: (value, getTagProps) => (_jsx(MultiSelectSelectedTags, { value: value, getTagProps: getTagProps, limitTags: props.limitTags })), groupBy: shouldGroup ? (option) => option.group : undefined, renderGroup: shouldGroup
            ? (params) => (_jsxs("li", { children: [_jsx(GroupHeader, { children: params.group }), _jsx(GroupItems, { children: params.children })] }, params.key))
            : undefined }, props), ((options === null || options === void 0 ? void 0 : options.length) ? options === null || options === void 0 ? void 0 : options.length : 0) + (isControlled ? 1 : 0)));
};
export default MultiSelect;
